import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FOOD_TRAILS } from '../data/foodTrails';
import { ALBERTA_RESTAURANTS, ALBERTA_FOOD_TRUCKS } from '../data/restaurants';
import { getRestaurantImage } from '../lib/foodImages';
import { setMeta } from '../lib/seo';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';
import Footer from '../components/Footer';

const ALL = [...ALBERTA_RESTAURANTS, ...ALBERTA_FOOD_TRUCKS];

export default function FoodTrail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [copied, setCopied] = useState(false);
  const [savedSet, setSavedSet] = useState(new Set());
  const trail = FOOD_TRAILS.find((t) => t.id === id);
  const stops = (trail?.stops || []).map((sid) => ALL.find((r) => r.id === sid)).filter(Boolean);
  const saved = trail ? savedSet.has(trail.id) : false;

  useEffect(() => {
    if (!isAuthenticated) { setSavedSet(new Set()); return; }
    apiFetch('/api/saved-trails')
      .then((list) => setSavedSet(new Set((list || []).map((s) => s.trail_id))))
      .catch(() => {});
  }, [isAuthenticated]);

  useEffect(() => {
    if (!trail || stops.length === 0) return;
    setMeta({
      title: `${trail.title} — ${trail.city} Food Trail | Culinera`,
      description: `${trail.blurb} A self-guided ${trail.city} food crawl with ${stops.length} stops, curated by Culinera.`,
      canonical: `https://culinera.ca/trails/${trail.id}`,
      image: getRestaurantImage(stops[0], 1200),
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: `${trail.title} — ${trail.city} Food Trail`,
        description: trail.blurb,
        itemListElement: stops.map((r, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          url: `https://culinera.ca/restaurants/${r.id}`,
          name: r.name,
        })),
      },
    });
  }, [trail, stops]);

  if (!trail || stops.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
        <div className="bg-white rounded-lg shadow p-8 text-center max-w-md">
          <h1 className="text-2xl font-bold mb-2">Trail not found</h1>
          <Link to="/" className="inline-block bg-teal-600 text-white px-4 py-2 rounded-lg font-semibold">← Back to Culinera</Link>
        </div>
      </div>
    );
  }

  // Real Google Maps route across all stops (Google computes actual times/directions).
  const mapsUrl =
    'https://www.google.com/maps/dir/' +
    stops.map((r) => encodeURIComponent(`${r.name}, ${r.city}, Alberta`)).join('/');

  const share = async () => {
    const url = `${window.location.origin}/trails/${trail.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: trail.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      }
    } catch {}
  };

  const toggleSave = async () => {
    if (!isAuthenticated) {
      navigate('/?signin=1');
      return;
    }
    try {
      if (saved) {
        await apiFetch(`/api/saved-trails/${trail.id}`, { method: 'DELETE' });
        setSavedSet((s) => { const n = new Set(s); n.delete(trail.id); return n; });
      } else {
        await apiFetch('/api/saved-trails', { method: 'POST', body: { trail_id: trail.id } });
        setSavedSet((s) => new Set(s).add(trail.id));
      }
    } catch {}
  };

  const mapQuery = encodeURIComponent(`${stops[0].name}, ${stops[0].city}, Alberta`);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white/90 backdrop-blur sticky top-0 z-30 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center py-4">
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-teal-600 to-cyan-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">C</span>
            </div>
            <span className="text-xl font-bold text-gray-900">Culinera</span>
          </Link>
          <Link to="/" className="text-teal-600 hover:text-teal-700 font-semibold">← Home</Link>
        </div>
      </header>

      {/* Hero: 3-up image strip */}
      <div className="relative w-full h-64 sm:h-80 overflow-hidden">
        <div className="absolute inset-0 grid grid-cols-3">
          {stops.slice(0, 3).map((r) => (
            <img key={r.id} src={getRestaurantImage(r, 800)} alt="" className="w-full h-full object-cover" />
          ))}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/20" />
        <div className="absolute inset-x-0 bottom-0">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
            <nav className="text-xs text-white/70 mb-2" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-white">Home</Link> › <span>Food Trails</span> › <span className="text-white">{trail.title}</span>
            </nav>
            <span className="text-xs font-bold bg-teal-500 text-white px-3 py-1 rounded-full">{trail.city} · {stops.length} stops</span>
            <h1 className="text-3xl sm:text-5xl font-bold text-white leading-tight mt-3 drop-shadow-sm">{trail.title}</h1>
            <p className="text-white/85 mt-1 text-sm sm:text-base">{trail.tagline}</p>
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8 mb-6">
          <p className="text-gray-700 text-lg leading-relaxed mb-6">{trail.blurb}</p>
          <div className="flex flex-wrap gap-3">
            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="trail-maps-route"
              className="inline-flex items-center gap-2 bg-teal-600 text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-teal-700 transition-colors"
            >🗺️ Open the full route in Google Maps</a>
            <button
              data-testid="trail-plan-wizard"
              onClick={() => navigate(`/?view=wizard&city=${encodeURIComponent(trail.city)}`)}
              className="inline-flex items-center gap-2 bg-white border border-teal-200 text-teal-700 font-semibold px-5 py-2.5 rounded-xl hover:bg-teal-50 transition-colors"
            >✨ Build a full itinerary</button>
            <button
              data-testid="trail-share"
              onClick={share}
              className="inline-flex items-center gap-2 bg-gray-100 text-gray-800 font-semibold px-5 py-2.5 rounded-xl hover:bg-gray-200 transition-colors"
            >{copied ? '✓ Link copied' : '🔗 Share trail'}</button>
            <button
              data-testid="trail-save"
              onClick={toggleSave}
              className={`inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-xl transition-colors border ${
                saved ? 'bg-teal-600 text-white border-teal-600 hover:bg-teal-700' : 'bg-white text-teal-700 border-teal-200 hover:bg-teal-50'
              }`}
            >{saved ? '✓ Saved to profile' : '♥ Save trail'}</button>
          </div>
        </div>

        {/* Map preview */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-6">
          <iframe
            data-testid="trail-map"
            title={`Map of ${trail.title}`}
            src={`https://www.google.com/maps?q=${mapQuery}&z=13&output=embed`}
            className="w-full h-64 sm:h-72 border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
          <div className="px-4 py-3 text-xs text-gray-500 flex items-center justify-between">
            <span>Map centred on {stops[0].neighborhood ? `${stops[0].neighborhood}, ` : ''}{trail.city}</span>
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="text-teal-600 font-semibold hover:underline">Full route & drive times →</a>
          </div>
        </div>

        {/* Stops */}
        <div className="space-y-5">
          {stops.map((r, i) => (
            <div key={r.id} data-testid={`trail-detail-stop-${r.id}`} className="bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col sm:flex-row">
              <Link to={`/restaurants/${r.id}`} className="sm:w-56 h-40 sm:h-auto shrink-0 relative overflow-hidden group">
                <img src={getRestaurantImage(r, 600)} alt={r.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                <span className="absolute top-3 left-3 w-8 h-8 rounded-full bg-teal-600 text-white font-bold flex items-center justify-center shadow">{i + 1}</span>
              </Link>
              <div className="p-5 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">{r.price}</span>
                  <span className="text-xs font-semibold bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full">{r.cuisine}</span>
                </div>
                <Link to={`/restaurants/${r.id}`} className="text-xl font-bold text-gray-900 hover:text-teal-700 transition-colors">{r.name}</Link>
                <div className="text-sm text-gray-500 mb-2">{r.neighborhood ? `${r.neighborhood}, ` : ''}{r.city}</div>
                <p className="text-gray-600 text-sm leading-relaxed">{r.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Other trails */}
        <section className="mt-12">
          <h2 className="text-xl font-bold text-gray-900 mb-4">More Alberta Food Trails</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {FOOD_TRAILS.filter((t) => t.id !== trail.id).map((t) => {
              const first = ALL.find((r) => r.id === t.stops[0]);
              return (
                <Link key={t.id} to={`/trails/${t.id}`} data-testid={`other-trail-${t.id}`} className="group bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-lg transition-all block">
                  <div className="relative h-28 overflow-hidden">
                    <img src={getRestaurantImage(first, 500)} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    {savedSet.has(t.id) && (
                      <span data-testid={`other-trail-saved-${t.id}`} className="absolute top-2 right-2 text-[11px] font-bold bg-teal-600 text-white px-2 py-0.5 rounded-full shadow">Saved ✓</span>
                    )}
                    <div className="absolute bottom-2 left-3 right-3">
                      <div className="text-white font-bold text-sm leading-tight">{t.title}</div>
                      <div className="text-white/70 text-xs">{t.city}</div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
